import { usePortalMutation } from 'src/Libs/api'
import {
  createNewDeviceLink,
  deleteDeviceLink,
  DeviceLinkModel,
  updateDeviceLink,
  updateEmcMiddleware,
  UpdateEmcMiddlewarePayload,
} from 'src/Libs/installations'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { UseMutationResult, useQueryClient } from '@tanstack/react-query'
import { installationDevicesKey } from 'src/Libs/flow/installation/hooks'
import { getDeviceLinksQueryKey } from 'src/Libs/installations/hooks/use-get-device-links'

interface CreateDeviceLinkVariables {
  installationId: number
  deviceId: string
}

interface DeleteDeviceLinkVariables {
  installationId: number
  deviceLinkId: string
}

interface UpdateDeviceLinkVariables {
  installationId: number
  deviceLinkId: string
  payload: Partial<DeviceLinkModel>
}

interface UpdateEmcMiddlewareVariables {
  installationId: number
  deviceLinkId: string
  payload: UpdateEmcMiddlewarePayload
}

interface UseDeviceLinkActionsResult {
  createDeviceLinkMutation: UseMutationResult<number | null, unknown, CreateDeviceLinkVariables>
  deleteDeviceLinkMutation: UseMutationResult<void | null, unknown, DeleteDeviceLinkVariables>
  updateDeviceLinkMutation: UseMutationResult<void | null, unknown, UpdateDeviceLinkVariables>
  updateEmcMiddlewareMutation: UseMutationResult<void | null, unknown, UpdateEmcMiddlewareVariables>
}

export const useDeviceLinkActions = (): UseDeviceLinkActionsResult => {
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()

  const invalidateDeviceLinkQueries = (installationId: number): Promise<void[]> =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: installationDevicesKey(installationId) }),
      queryClient.invalidateQueries({ queryKey: getDeviceLinksQueryKey(installationId.toString()) }),
    ])

  const createDeviceLinkMutation = usePortalMutation({
    mutationFn: ({ installationId, deviceId }: CreateDeviceLinkVariables) =>
      createNewDeviceLink(authFetch, installationId, {
        deviceId,
        deviceType: 'emc',
        isEmsMaster: true,
      }),
    onSuccess: (_data, { installationId }) => invalidateDeviceLinkQueries(installationId),
  })

  const deleteDeviceLinkMutation = usePortalMutation({
    mutationFn: ({ installationId, deviceLinkId }: DeleteDeviceLinkVariables) => deleteDeviceLink(installationId, deviceLinkId, authFetch),
    onSuccess: (_data, { installationId }) => invalidateDeviceLinkQueries(installationId),
  })

  const updateDeviceLinkMutation = usePortalMutation({
    mutationFn: ({ installationId, deviceLinkId, payload }: UpdateDeviceLinkVariables) =>
      updateDeviceLink(installationId, deviceLinkId, payload, authFetch),
    onSuccess: (_data, { installationId }) => invalidateDeviceLinkQueries(installationId),
  })

  const updateEmcMiddlewareMutation = usePortalMutation({
    mutationFn: ({ installationId, deviceLinkId, payload }: UpdateEmcMiddlewareVariables) =>
      updateEmcMiddleware(installationId, deviceLinkId, payload, authFetch),
    onSuccess: (_data, { installationId }) => invalidateDeviceLinkQueries(installationId),
  })

  return { createDeviceLinkMutation, deleteDeviceLinkMutation, updateDeviceLinkMutation, updateEmcMiddlewareMutation }
}
